import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, CircularProgress, Paper } from "@mui/material";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import { DriverData } from "../../../vendor/vehicleAdmin/vehicleAuditComponents/types";

interface MapProps {
  data: DriverData[] | undefined; // Data to be displayed on the map
  jobType: string | undefined; // Type of job for conditional rendering
  city: string | undefined; // Optional city for additional context
}

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const ClusteredMap: React.FC<MapProps> = ({ data, jobType, city }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [cityCoordinates, setCityCoordinates] = useState<[number, number]>([12.9716, 77.5946]);
  const leafletMap = useRef<L.Map | null>(null);
  const controlRef = useRef<L.Control | null>(null);

  // Geocoding function
  const geocodeCity = async (cityName: string): Promise<[number, number] | null> => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityName)}&limit=1`);
      const data = await response.json();
      return data?.[0] ? [parseFloat(data[0].lat), parseFloat(data[0].lon)] : null;
    } catch (error) {
      console.error("Error geocoding city:", error);
      return null;
    }
  };

  // Format date to be more readable
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Extract popup content creation to avoid duplication
  const createPopupContent = (item: DriverData): string => {
    const location = item["Installation Location"];
    const driver = item["Driver Details: "];
    const installerName = item["installerName"];

    const locationLatitudeText = location.latitude
      ? `<p>Latitude: ${location.latitude}</p>`
      : "";
    const locationLongitudeText = location.longitude
      ? `<p>Longitude: ${location.longitude}</p>`
      : "";

    if (jobType !== "auto_branding") {
      return `
        <div>
          <p>Installer Name: ${installerName}</p>
          ${locationLatitudeText}
          ${locationLongitudeText}
          <p>Date: ${formatDate(item.date)}</p>
          <p>
            <a
              href="https://www.google.com/maps?q=${location.latitude},${location.longitude}"
              target="_blank"
              rel="noopener noreferrer"
            >
              View on Google Maps
            </a>
          </p>
        </div>
      `;
    } else {
      return `<div>
        <p>Installer Name: ${installerName}</p>
        <p>Driver Name: ${driver.name}</p>
        <p>RC Number: ${driver.rcNumber.toUpperCase()}</p>
        <p>Phone: ${driver.phoneNumber}</p>
        ${locationLatitudeText}
        ${locationLongitudeText}
        <p>Date: ${formatDate(item.date)}</p>
        <p>
          <a 
            href="https://www.google.com/maps?q=${location.latitude},${location.longitude}"
            target="_blank"
            rel="noopener noreferrer"
          >
            View on Google Maps
          </a>
        </p> 
      </div>
      `;
    }
  };

  // Extract marker creation to avoid duplication
  const createMarkerWithPopup = (item: DriverData): L.Marker | null => {
    const location = item["Installation Location"];
    
    if (!location || !location.latitude || !location.longitude) {
      return null;
    }

    const latLng = L.latLng(location.latitude, location.longitude);
    const marker = L.marker(latLng);
    marker.bindPopup(createPopupContent(item));
    return marker;
  };

  // Effect to geocode city and initialize map
  useEffect(() => {
    const initializeWithCity = async () => {
      if (city?.trim()) {
        const coordinates = await geocodeCity(city.trim());
        if (coordinates) setCityCoordinates(coordinates);
      }
      
      try {
        await import("leaflet.markercluster");
        initializeMap();
      } catch (err) {
        console.error("Error loading marker cluster:", err);
        setError("Failed to load map components");
        setLoading(false);
      }
    };

    initializeWithCity();

    return () => {
      if (leafletMap.current) {
        if (controlRef.current) {
          leafletMap.current.removeControl(controlRef.current);
          controlRef.current = null;
        }
        leafletMap.current.remove();
        leafletMap.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city]);

  useEffect(() => {
    // Update markers when data changes
    if (leafletMap.current && data && data.length > 0) {
      updateMarkers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const initializeMap = () => {
    if (!mapRef.current || leafletMap.current) return;

    try {
      const map = L.map(mapRef.current).setView(cityCoordinates, 11);
      leafletMap.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19,
      }).addTo(map);

      addCustomControl(map);
      updateMarkers();
      setLoading(false);
    } catch (err) {
      console.error("Error initializing map:", err);
      setError("Failed to initialize map");
      setLoading(false);
    }
  };

  // Add custom control separately - only once
  const addCustomControl = (map: L.Map) => {
    // Create the custom uncluster control once
    const UnclusterControl = L.Control.extend({
      options: {
        position: "topright",
      },

      onAdd: function (map: L.Map) {
        const container = L.DomUtil.create("div", "leaflet-control-uncluster");
        const button = L.DomUtil.create("button", "", container);
        button.innerHTML = "Show All Markers";
        button.style.padding = "8px";
        button.style.backgroundColor = "white";
        button.style.border = "1px solid #ccc";
        button.style.borderRadius = "4px";
        button.style.cursor = "pointer";

        // The toggle function will be managed by a closure
        // These references will need to be updated by updateMarkers()
        (container as any)._showingClusters = true;
        (container as any)._markers = null as L.MarkerClusterGroup | null;
        (container as any)._individualMarkers = [] as L.Marker[];

        // Store button reference so we can update it later
        (container as any)._button = button;

        const toggleClusters = function (e: Event) {
          L.DomEvent.preventDefault(e);

          const showingClusters = (container as any)
            ._showingClusters as boolean;
          const markers = (container as any)
            ._markers as L.MarkerClusterGroup | null;
          let individualMarkers = (container as any)
            ._individualMarkers as L.Marker[];

          if (showingClusters && markers) {
            // Remove the cluster layer
            map.removeLayer(markers);

            // Add individual markers to the map
            individualMarkers = [];
            data?.forEach((item) => {
              const marker = createMarkerWithPopup(item);
              if (marker) {
                marker.addTo(map);
                individualMarkers.push(marker);
              }
            });

            // Store individual markers reference
            (container as any)._individualMarkers = individualMarkers;

            // Change button text
            button.innerHTML = "Group Markers";
            (container as any)._showingClusters = false;
          } else {
            // Remove individual markers
            individualMarkers.forEach((marker: L.Marker) =>
              map.removeLayer(marker)
            );
            (container as any)._individualMarkers = [] as L.Marker[];

            // Add back the cluster layer if available
            if (markers) {
              map.addLayer(markers);
            }

            // Reset button text
            button.innerHTML = "Show All Markers";
            (container as any)._showingClusters = true;
          }
        };

        // Use proper event handling
        L.DomEvent.on(button, "click", toggleClusters)
          .on(container, "dblclick", L.DomEvent.stopPropagation)
          .on(container, "click", L.DomEvent.stopPropagation)
          .on(container, "mousedown", L.DomEvent.stopPropagation);

        return container;
      },
    });

    // Create and add the control
    const control = new UnclusterControl();
    control.addTo(map);

    // Store control reference for later cleanup
    controlRef.current = control;
  };

  const updateMarkers = () => {
    if (!leafletMap.current || !data || data.length === 0) return;

    try {
      const map = leafletMap.current;

      // Get control container if it exists
      const controlContainer =
        controlRef.current && (controlRef.current as any).getContainer();
      const showingClusters = controlContainer
        ? ((controlContainer as any)._showingClusters as boolean)
        : true;
      const individualMarkers = controlContainer
        ? ((controlContainer as any)._individualMarkers as L.Marker[]) || []
        : [];

      // Clear existing markers - conditional based on control state
      if (showingClusters) {
        map.eachLayer((layer: L.Layer) => {
          if (
            (layer as any).getAllChildMarkers &&
            typeof (layer as any).getAllChildMarkers === "function"
          ) {
            map.removeLayer(layer);
          }
        });
      } else {
        // Remove individual markers if we're not showing clusters
        individualMarkers.forEach((marker: L.Marker) =>
          map.removeLayer(marker)
        );
        if (controlContainer) {
          (controlContainer as any)._individualMarkers = [] as L.Marker[];
        }
      }

      // Create a marker cluster group with more aggressive options
      const markers = (window as any).L.markerClusterGroup({
        disableClusteringAtZoom: 19, // Disable clustering at max zoom
        maxClusterRadius: 50, // Smaller radius means less clustering
        spiderfyOnMaxZoom: true, // Spiderfy markers at max zoom
        zoomToBoundsOnClick: true, // Zoom to bounds when cluster clicked
      });

      // Calculate bounds to fit all markers
      const bounds = L.latLngBounds([]);

      // Add markers for all valid locations
      data.forEach((item) => {
        const location = item["Installation Location"];
        
        if (!location || !location.latitude || !location.longitude) {
          return;
        }

        const latLng = L.latLng(location.latitude, location.longitude);
        bounds.extend(latLng);

        // Create marker (either to cluster group or individual)
        const marker = createMarkerWithPopup(item);
        if (!marker) return;

        if (showingClusters) {
          markers.addLayer(marker);
        } else {
          marker.addTo(map);
          if (controlContainer) {
            const currentMarkers = (controlContainer as any)
              ._individualMarkers as L.Marker[];
            currentMarkers.push(marker);
          }
        }
      });

      // Add the marker cluster group to the map if showing clusters
      if (showingClusters) {
        map.addLayer(markers);
      }

      // Fit bounds to data if available, otherwise keep city focus
      if (bounds.getNorthEast() && bounds.getSouthWest() && 
          bounds.getNorthEast().lat !== bounds.getSouthWest().lat && 
          bounds.getNorthEast().lng !== bounds.getSouthWest().lng) {
        map.fitBounds(bounds, { padding: [50, 50] });
      } else {
        map.setView(cityCoordinates, 11);
      }

      // Update markers reference in control
      if (controlContainer) {
        (controlContainer as any)._markers = markers;
      }
    } catch (err) {
      console.error("Error updating markers:", err);
      setError("Failed to update map markers");
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{ position: "relative", height: "500px", width: "100%" }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
          }}
        >
          <Typography color="error">{error}</Typography>
        </Box>
      )}

      <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
    </Paper>
  );
};

export default ClusteredMap;